import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Debounced autosave hook.
 *
 * Triggers `saveFn` after `delay` ms of inactivity whenever `deps` change.
 * The first render is always skipped — autosave only fires on real user changes.
 *
 * @param {() => Promise<void>} saveFn  Async function that performs the save
 * @param {Array}               deps    Dependency array (same semantics as useEffect)
 * @param {object}              [opts]
 * @param {number}              [opts.delay=10000]   Debounce delay in ms
 * @param {boolean}             [opts.enabled=true] Set false to disable autosave entirely
 *
 * @returns {'idle'|'saving'|'saved'|'error'} Current autosave status
 */
export function useAutosave(saveFn, deps, { delay = 10000, enabled = true } = {}) {
    const [status, setStatus] = useState('idle');
    const timerRef      = useRef(null);
    const mountedRef    = useRef(true);
    const isFirstRun    = useRef(true);  // skip autosave on initial mount
    const saveFnRef     = useRef(saveFn);

    // Keep saveFnRef current without re-triggering the debounce
    useEffect(() => { saveFnRef.current = saveFn; });

    // Track mounted state so async callbacks never setState after unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            clearTimeout(timerRef.current);
        };
    }, []);

    const triggerSave = useCallback(async () => {
        if (!mountedRef.current) return;
        setStatus('saving');
        try {
            await saveFnRef.current();
            if (!mountedRef.current) return;
            setStatus('saved');
            // Auto-reset to idle after 10 s so the indicator fades
            timerRef.current = setTimeout(() => {
                if (mountedRef.current) setStatus('idle');
            }, 10000);
        } catch {
            if (mountedRef.current) setStatus('error');
        }
    }, []);

    useEffect(() => {
        // Skip the very first run (component just mounted — no user change yet)
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        if (!enabled) return;

        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(triggerSave, delay);

        return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...deps, enabled, delay]);

    return status;
}
